import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { TenantAovChartResponseDto } from '@/generated/korza-api-client/types.gen';
import { type ChartGranularity } from '@/lib/chart-axis';
import { useMappedChartData } from '@/lib/hooks/use-chart-data';
import { useChartTooltip } from '@/lib/hooks/use-chart-tooltip';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import { markPartialRows, type ChartRange } from '@/lib/partial-buckets';
import { MetricsAreaChart } from '@/components/ui/charts/area-chart';
import {
  MetricsChartAreaSeries,
  MetricsChartLineSeries,
} from '@/components/ui/charts/chart-area-series';
import { chartColor } from '@/lib/chart-colors';
import { ChartClickHint } from '@/components/ui/charts/chart-click-hint';

type TenantAovChartPointDto = TenantAovChartResponseDto['points'][number];

export type Granularity = ChartGranularity;

interface AverageTicketChartProps {
  granularity: Granularity;
  currency: string;
  points: TenantAovChartPointDto[];
  /** The filter's period: marks the partial first/last bucket (CHART-01). */
  range?: ChartRange;
  syncId?: string;
  syncMethod?: 'index' | 'value';
  /**
   * When provided, each period is clickable; `bucketStart` is the point's
   * original api date.
   */
  onBucketClick?: (bucketStart: string) => void;
}

const SITE_LINE_COLOR = 'hsl(var(--muted-foreground))';

export function AverageTicketChart({
  currency,
  granularity,
  points,
  range,
  syncId,
  syncMethod,
  onBucketClick,
}: AverageTicketChartProps) {
  const { LL, locale } = useI18nContext();

  const lineChartData = useMappedChartData(
    points,
    granularity,
    locale,
    (p) => ({
      date: p.date,
      appTicket: p.aovApp,
      webTicket: p.aovWeb,
    })
  );
  const partial = usePartialBuckets(lineChartData, range, granularity);
  const chartData = useMemo(
    () => markPartialRows(lineChartData, 'appTicket', partial.edges),
    [lineChartData, partial.edges]
  );

  const handleBucketClick = onBucketClick
    ? (row: Record<string, unknown>) => {
        if (typeof row.date === 'string') onBucketClick(row.date);
      }
    : undefined;

  const tooltipContent = useChartTooltip({
    footer: onBucketClick ? <ChartClickHint /> : undefined,
    // The dashed partial series shares the solid one's name: one row each.
    formatRows: (_label, payload) =>
      (payload ?? [])
        .filter(
          (entry, index, all) =>
            all.findIndex((other) => other.name === entry.name) === index
        )
        .map((entry) => ({
          label: String(entry.name ?? ''),
          value: new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
          }).format((entry.value as number) ?? 0),
        })),
  });

  return (
    <MetricsAreaChart
      data={chartData}
      partialLegend={partial.edges}
      legendLines={[
        { label: LL.metricsPage.salesTab.app(), color: chartColor(1) },
        { label: LL.metricsPage.salesTab.site(), color: SITE_LINE_COLOR },
      ]}
      syncId={syncId}
      syncMethod={syncMethod}
      yAxisOrientation="right"
      tooltipContent={tooltipContent}
      onBucketClick={handleBucketClick}
    >
      <MetricsChartAreaSeries
        dataKey="appTicket"
        name={LL.metricsPage.charts.averageTicket.title()}
        partialDataKey="appTicketPartial"
        partialLabels={partial.labels}
      />
      {/* The site's ticket (`aovWeb`) in gray (ANLT-04). */}
      <MetricsChartLineSeries
        dataKey="webTicket"
        name={LL.metricsPage.salesTab.site()}
        color={SITE_LINE_COLOR}
      />
    </MetricsAreaChart>
  );
}
